import { toValue, type MaybeRefOrGetter } from "vue";
import {
  useReferralService,
  type ReferralPayload,
} from "@/features/channel/services/use-referral-service";
import { useUserStore } from "@/stores/user.store";
import { useRouter } from "vue-router";
import { ExperienceType } from "@/generated/graphql";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import { getUrlOrAbsoluteUrlFromOrigin } from "@/utils/misc";
import { buildShareableAppUrl } from "@/utils/app-navigator";

export interface OpenGraphObjectPayload {
  title: string | undefined | null;
  description?: string | null;
  image?: string | null;
  type?: string | null;
}

export interface ReferralObjectPayload {
  title?: string;
  text?: string;
  payload: ReferralPayload;
}

export function useGetReferralLink({
  openGraphObject,
  referralObject,
}: {
  openGraphObject: MaybeRefOrGetter<OpenGraphObjectPayload>;
  referralObject: MaybeRefOrGetter<ReferralObjectPayload>;
}) {
  const channelStore = useChannelStore();
  const userStore = useUserStore();

  const referralService = useReferralService();
  const router = useRouter();

  async function getLinkForReferralPayload(
    payload: ReferralPayload,
  ): Promise<string> {
    const params: Record<string, string> = {
      channel: channelStore.channelSlug!,
    };
    let routeName = RouteNames.Channel;

    if (payload.experience_type && payload.experience_id) {
      switch (payload.experience_type) {
        case ExperienceType.Show:
          params.show = payload.experience_id;
          routeName = RouteNames.Show;
          break;
        case ExperienceType.Thread:
          params.thread_id = payload.experience_id;
          routeName = RouteNames.ThreadsItem;
          break;
        case ExperienceType.Raffle:
        case ExperienceType.Quiz:
          routeName = RouteNames.Channel;
          break;
      }
    }

    const routeHref = router.resolve({
      name: routeName,
      params: params,
    }).href;
    return buildShareableAppUrl(routeHref, {
      channelId: channelStore.channelSlug ?? undefined,
    });
  }

  async function getReferralLink(): Promise<string | null> {
    const value = toValue(referralObject);
    const openGraphPayload = toValue(openGraphObject);

    if (!userStore.isAuthenticated) {
      return getLinkForReferralPayload(value.payload);
    }

    const imageUrl = getUrlOrAbsoluteUrlFromOrigin(
      openGraphPayload.image || channelStore.channel?.logo || "/logo.png",
    );

    const link = await referralService.createReferralLink(
      {
        ...openGraphPayload,
        title:
          openGraphPayload.title ??
          value.title ??
          "Earn exclusive rewards in FanFest",
        image: imageUrl,
        type: openGraphPayload.type ?? "website",
      },
      value.payload,
    );

    return link;
  }

  return getReferralLink;
}
